/**
 * Client service for Hospital Operational Incidents & CAPA.
 * R39 C5.a4 (plan docs/plans/sector-operations.md §2 SO-P9, SO-P252, H§5).
 * Wraps callables via callable(); components never import firebase/*.
 */

import { callable } from './callable'
import type {
  IncidentKind,
  IncidentSeverity,
  IncidentState,
  OpsIncidentDoc,
  OpsPersonRef,
} from '../types/ops'

export interface ReportIncidentInput {
  workspaceId: string
  placeId: string
  kind: IncidentKind
  severity: IncidentSeverity
  occurredAt: string | number
  summary: string
  opId?: string
}

export interface ReviewIncidentInput {
  workspaceId: string
  incidentId: string
  reviewNotes?: string
  opId?: string
}

export interface AddCapaItemsInput {
  workspaceId: string
  incidentId: string
  capaItems: Array<{
    action: string
    ownerPersonRef: OpsPersonRef
    dueDay: string
  }>
  opId?: string
}

export interface CompleteCapaItemInput {
  workspaceId: string
  incidentId: string
  capaItemId: string
  opId?: string
}

export interface RecordEffectivenessInput {
  workspaceId: string
  incidentId: string
  effective: boolean
  effectivenessNotes?: string
  opId?: string
}

export interface ListIncidentsInput {
  workspaceId: string
  placeId?: string
  state?: IncidentState
  kind?: IncidentKind
  severity?: IncidentSeverity
}

export interface GetIncidentInput {
  workspaceId: string
  incidentId: string
}

export async function reportIncident(
  input: ReportIncidentInput,
): Promise<{ success: true; incidentId: string; replayed?: boolean }> {
  return callable<ReportIncidentInput, { success: true; incidentId: string; replayed?: boolean }>('reportIncident', input)
}

export async function reviewIncident(
  input: ReviewIncidentInput,
): Promise<{ success: true; incidentId: string; state: IncidentState; replayed?: boolean }> {
  return callable<ReviewIncidentInput, { success: true; incidentId: string; state: IncidentState; replayed?: boolean }>('reviewIncident', input)
}

export async function addCapaItems(
  input: AddCapaItemsInput,
): Promise<{ success: true; incidentId: string; capaCount: number; replayed?: boolean }> {
  return callable<AddCapaItemsInput, { success: true; incidentId: string; capaCount: number; replayed?: boolean }>('addCapaItems', input)
}

export async function completeCapaItem(
  input: CompleteCapaItemInput,
): Promise<{ success: true; incidentId: string; state: IncidentState; allDone: boolean; replayed?: boolean }> {
  return callable<CompleteCapaItemInput, { success: true; incidentId: string; state: IncidentState; allDone: boolean; replayed?: boolean }>('completeCapaItem', input)
}

export async function recordEffectiveness(
  input: RecordEffectivenessInput,
): Promise<{ success: true; incidentId: string; state: IncidentState; replayed?: boolean }> {
  return callable<RecordEffectivenessInput, { success: true; incidentId: string; state: IncidentState; replayed?: boolean }>('recordEffectiveness', input)
}

export async function listIncidents(
  input: ListIncidentsInput,
): Promise<{ items: OpsIncidentDoc[] }> {
  return callable<ListIncidentsInput, { items: OpsIncidentDoc[] }>('listIncidents', input)
}

export async function getIncident(
  input: GetIncidentInput,
): Promise<{ incident: OpsIncidentDoc }> {
  return callable<GetIncidentInput, { incident: OpsIncidentDoc }>('getIncident', input)
}

export interface SetIncidentSettingsInput {
  workspaceId: string
  reportWithinHours: number | null
  opId?: string
}

export interface GetIncidentSettingsInput {
  workspaceId: string
}

export interface IncidentSettings {
  reportWithinHours: number | null
}

export async function setIncidentSettings(
  input: SetIncidentSettingsInput,
): Promise<{ success: true; reportWithinHours: number | null; replayed?: boolean }> {
  return callable<SetIncidentSettingsInput, { success: true; reportWithinHours: number | null; replayed?: boolean }>(
    'setIncidentSettings',
    input,
  )
}

export async function getIncidentSettings(
  input: GetIncidentSettingsInput,
): Promise<{ settings: IncidentSettings }> {
  return callable<GetIncidentSettingsInput, { settings: IncidentSettings }>(
    'getIncidentSettings',
    input,
  )
}
